import React, { useState, useEffect } from 'react'
import { MapPin, Navigation } from 'lucide-react'
import { toast } from 'react-toastify'
import { geolocationService } from '../../services/geolocationService'
import { SafariGeolocationService } from '../../services/safariGeolocationService'
import { useLocations } from '../../utils/LocationContext'
import './GeolocationField.css'

const GeolocationField = ({
    value,
    onChange,
    onCoordinatesChange,
    onSmartLocationChange, // Nueva prop para pasar el objeto SmartLocation completo
    label = "Ubicación",
    required = false,
    allowCustom = true
}) => {
    const { locations } = useLocations()
    const [geoLocationStatus, setGeoLocationStatus] = useState('idle')
    const [showLocationPrompt, setShowLocationPrompt] = useState(false)
    const [showCustomLocation, setShowCustomLocation] = useState(false)
    const [customLocation, setCustomLocation] = useState('')
    const [isSafari, setIsSafari] = useState(false)

    // Detectar Safari al montar el componente
    useEffect(() => {
        setIsSafari(SafariGeolocationService.isSafari())
    }, [])

    const requestExactLocation = async () => {
        setGeoLocationStatus('requesting-exact')

        // Verificar contexto seguro para Safari
        if (isSafari && !SafariGeolocationService.isSecureContext()) {
            toast.error('Necesitas una conexión segura (HTTPS) para acceder a tu ubicación.', {
                position: "top-center",
                autoClose: 6000,
            })
            setGeoLocationStatus('error')
            return
        }

        try {
            const coords = await geolocationService.getExactLocation()
            await handleGeolocationSuccess(coords, 'exact')
        } catch (error) {
            console.log('Ubicación exacta denegada:', error.message)
            setShowLocationPrompt(true)
        }
    }

    const requestApproximateLocation = async () => {
        setGeoLocationStatus('requesting-approximate')
        setShowLocationPrompt(false)

        try {
            const coords = await geolocationService.getApproximateLocation()
            await handleGeolocationSuccess(coords, 'approximate')
        } catch (error) {
            const errorMessage = 'No se pudo obtener tu ubicación. Verifica los permisos o selecciona manualmente.'

            toast.error(errorMessage, {
                position: "top-center",
                autoClose: 4000,
            })
            setGeoLocationStatus('error')
            setShowLocationPrompt(false)
        }
    }

    const handleGeolocationSuccess = async (coords, type) => {
        try {

            // Pasar las coordenadas al componente padre SIEMPRE
            if (onCoordinatesChange) {
                onCoordinatesChange(coords)
            }

            // Intentar encontrar ubicación más cercana en la lista
            const closestLocation = geolocationService.findClosestLocation(coords, locations)

            if (closestLocation) {
                onChange(closestLocation.name)
                setGeoLocationStatus('success')

                // Pasar SmartLocation si está disponible el callback
                if (onSmartLocationChange) {
                    const smartLocationData = {
                        approximate: type.includes('approximate') || type.includes('safari'),
                        coordinates: {
                            lat: coords.latitude,
                            lng: coords.longitude
                        },
                        displayName: closestLocation.name,
                        lastUpdated: new Date().toISOString(),
                        originalLocation: closestLocation.name,
                        serviceLocationId: closestLocation.id || null,
                        source: isSafari ? "GPS-Safari" : "GPS"
                    };
                    onSmartLocationChange(smartLocationData);
                }

                toast.success(`📍 Ubicación detectada: ${closestLocation.name}`, {
                    position: "top-center",
                    autoClose: 3000,
                })
                return
            }

            // Si no encuentra ubicación cercana, usar geocoding
            const geoData = await geolocationService.reverseGeocode(coords.latitude, coords.longitude)

            // Pasar el SmartLocation completo desde LocationIQ
            if (onSmartLocationChange && geoData.smartLocation) {
                // Ajustar el source para Safari internamente
                if (isSafari) {
                    geoData.smartLocation.source = "GPS-Safari"
                    geoData.smartLocation.approximate = true
                }
                onSmartLocationChange(geoData.smartLocation)
            }

            const locationByCity = geolocationService.findLocationByCity(geoData.city, locations)

            if (locationByCity) {
                onChange(locationByCity.name)
                setGeoLocationStatus('success')

                toast.info(`📍 Te ubicamos en ${locationByCity.name} según tu zona`, {
                    position: "top-center",
                    autoClose: 4000,
                })
            } else if (!geoData.isCoordinatesOnly) {
                // Usar la ubicación detectada directamente
                const detectedLocation = geoData.displayName || geoData.city || 'Ubicación detectada'

                onChange(detectedLocation)
                setGeoLocationStatus('success')

                toast.success(`📍 Ubicación detectada: ${detectedLocation}`, {
                    position: "top-center",
                    autoClose: 4000,
                })
            } else {
                // Fallback a coordenadas si LocationIQ falla
                const coordinatesText = `Lat: ${coords.latitude.toFixed(4)}, Lon: ${coords.longitude.toFixed(4)}`
                onChange(coordinatesText)
                setGeoLocationStatus('success')

                // Incluso para coordenadas, crear SmartLocation básico
                if (onSmartLocationChange) {
                    const basicSmartLocation = {
                        approximate: type.includes('approximate') || isSafari,
                        coordinates: {
                            lat: coords.latitude,
                            lng: coords.longitude
                        },
                        displayName: coordinatesText,
                        lastUpdated: new Date().toISOString(),
                        originalLocation: null,
                        serviceLocationId: null,
                        source: isSafari ? "GPS-Safari" : "GPS"
                    };
                    onSmartLocationChange(basicSmartLocation);
                }

                toast.warning(`📍 Ubicación detectada (coordenadas): ${coordinatesText}`, {
                    position: "top-center",
                    autoClose: 4000,
                })
            }

        } catch (error) {
            console.error('❌ GeolocationField - Error processing location:', error)

            toast.error('Error al procesar tu ubicación. Puedes escribir tu ubicación manualmente.', {
                position: "top-center",
                autoClose: 4000,
            })
            setGeoLocationStatus('error')
        }
    }

    const useManualSelection = () => {
        setShowLocationPrompt(false)
        setGeoLocationStatus('idle')
    }

    const handleLocationChange = (e) => {
        const selectedValue = e.target.value

        if (selectedValue === 'custom') {
            setShowCustomLocation(true)
            // No notificar al padre aún, esperar a que escriba en el campo personalizado
            setCustomLocation('')
        } else {
            setShowCustomLocation(false)
            setCustomLocation('')
            onChange(selectedValue)
        }
        setGeoLocationStatus('idle')
    }

    const handleCustomLocationChange = (e) => {
        const newValue = e.target.value
        setCustomLocation(newValue)
        // Siempre notificar al componente padre con lo que está escribiendo
        onChange(newValue)
    }

    // Effect para sincronizar el estado cuando el valor externo cambia
    useEffect(() => {
        if (value === 'custom') {
            setShowCustomLocation(true)
        } else if (value && customLocation !== value) {
            // Si el valor externo no está en la lista de ubicaciones, podría ser personalizado
            const isValueInList = Array.isArray(locations) && locations.some(loc => (loc.name || loc) === value)
            if (!isValueInList && value !== '') {
                setShowCustomLocation(true)
                setCustomLocation(value)
            } else {
                setShowCustomLocation(false)
                setCustomLocation('')
            }
        } else if (!value) {
            setShowCustomLocation(false)
            setCustomLocation('')
        }
    }, [value, locations, customLocation])

    return (
        <div className="geolocation-field">
            <div className="location-input-wrapper">
                <MapPin className="location-input-icon" size={18} />

                <select
                    className={`form-control geolocation-select ${!value ? 'no-selection' : ''}`}
                    value={showCustomLocation ? 'custom' : value}
                    onChange={handleLocationChange}
                    required={required}
                >
                    <option value="" disabled hidden>
                        {geoLocationStatus.includes('requesting')
                            ? 'Detectando tu ubicación...'
                            : `Seleccionar ${label.toLowerCase()}`
                        }
                    </option>

                    {/* Si hay un valor que NO está en la lista y NO se está usando el campo personalizado, mostrarlo como opción */}
                    {value && !showCustomLocation && (!Array.isArray(locations) || !locations.some(loc => (loc.name || loc) === value)) ? (
                        <option key="detected-location" value={value}>
                            {value}
                        </option>
                    ) : null}

                    {Array.isArray(locations) &&
                        locations.map((loc) => (
                            <option key={loc.name || loc} value={loc.name || loc}>
                                {loc.name || loc}
                            </option>
                        ))}
                    {allowCustom && (
                        <option value="custom">Otra ubicación</option>
                    )}
                </select>

                <button
                    type="button"
                    className="geolocation-field-btn"
                    onClick={requestExactLocation}
                    disabled={geoLocationStatus === 'requesting-exact' || geoLocationStatus === 'requesting-approximate'}
                    title="Detectar mi ubicación automáticamente"
                >
                    <Navigation
                        size={16}
                        className={geoLocationStatus.includes('requesting') ? 'location-loading' : ''}
                    />
                    {!geoLocationStatus.includes('requesting') && (
                        <span className={`geo-btn-text ${isSafari ? 'safari-mode' : ''}`}>GPS</span>
                    )}
                </button>
            </div>

            {/* Campo personalizado */}
            {showCustomLocation && (
                <div className="custom-location-wrapper">
                    <input
                        type="text"
                        className="form-control custom-location-input"
                        placeholder="Escribe tu ubicación"
                        value={customLocation}
                        onChange={handleCustomLocationChange}
                        required={value === 'custom'}
                    />
                </div>
            )}

            {/* Prompt para ubicación aproximada */}
            {showLocationPrompt && (
                <div className="geolocation-prompt">
                    <p><strong>¿Podemos acceder a tu zona general?</strong></p>
                    <p>Solo usaremos tu ciudad, no tu ubicación exacta</p>
                    <div className="prompt-buttons">
                        <button
                            type="button"
                            onClick={requestApproximateLocation}
                            className="btn-primary-small"
                        >
                            Sí, usar zona
                        </button>
                        <button
                            type="button"
                            onClick={useManualSelection}
                            className="btn-secondary-small"
                        >
                            Elegir manualmente
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default GeolocationField
